import { AlertCircle, Camera, CheckCircle, DollarSign, Package, Route, Truck, User, X } from "lucide-react";
import { getStatusButtonText } from "../utilities/utilities";

// Course Details Modal
export const CourseDetailsModal = ({ show, course, onClose, onCMRUpload, onUpdateStatus }) => {
  if (!show || !course) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end">
      <div className="bg-gradient-to-b from-slate-900/95 to-slate-800/95 backdrop-blur-xl rounded-t-3xl border-t-2 border-white/10 w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-b from-slate-900 to-slate-900/90 backdrop-blur-lg p-5 sm:p-6 border-b border-white/5">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold text-2xl">Detalii cursă</h3>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-gray-300 hover:text-white transition-all active:scale-95"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6 space-y-3">
          {/* Traseu */}
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-2xl p-5 border border-blue-500/20 shadow-lg">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-blue-500/30 rounded-xl flex items-center justify-center">
                <Route className="w-5 h-5 text-blue-300" />
              </div>
              <span className="text-white font-semibold text-lg">Traseu</span>
            </div>
            <p className="text-xl text-white font-bold mb-1">
              {course.from} → {course.to}
            </p>
            {course.details.progress > 0 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm text-blue-200 mb-2">
                  <span className="font-medium">Progres</span>
                  <span className="font-bold">{course.details.progress}%</span>
                </div>
                <div className="w-full bg-slate-700/50 rounded-full h-3 overflow-hidden shadow-inner">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-blue-400 h-3 rounded-full transition-all duration-500 shadow-lg" 
                    style={{ width: `${course.details.progress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Client */}
          <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-2xl p-5 border border-green-500/20 shadow-lg">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-green-500/30 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-green-300" />
              </div>
              <span className="text-white font-semibold text-lg">Client</span>
            </div>
            <p className="text-white text-lg font-medium">{course.client}</p>
          </div>

          {/* Vehicul */}
          {course.vehicleNumber && (
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-2xl p-5 border border-purple-500/20 shadow-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-purple-500/30 rounded-xl flex items-center justify-center">
                  <Truck className="w-5 h-5 text-purple-300" />
                </div>
                <span className="text-white font-semibold text-lg">Vehicul</span>
              </div>
              <p className="text-white text-lg font-medium">{course.vehicleName || 'Vehicul'}</p>
              <p className="text-purple-200 text-sm mt-1">{course.vehicleNumber}</p>
            </div>
          )}

          {/* Mărfuri */}
          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-2xl p-5 border border-orange-500/20 shadow-lg">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-orange-500/30 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-orange-300" />
              </div>
              <span className="text-white font-semibold text-lg">Mărfuri</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Tip:</span>
                <span className="text-white font-medium">{course.details.goods}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Greutate:</span>
                <span className="text-white font-medium">{course.details.weight}</span>
              </div>
              {course.details.priority && (
                <div className="flex justify-between items-center pt-1">
                  <span className="text-gray-400">Prioritate:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    course.details.priority === 'Urgentă' 
                      ? 'bg-red-500/30 text-red-200 border border-red-500/30' 
                      : 'bg-blue-500/30 text-blue-200 border border-blue-500/30'
                  }`}>
                    {course.details.priority}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Plată */}
          {course.amount > 0 && course.paymentType === 'cash' && (
            <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-2xl p-5 border border-yellow-500/20 shadow-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-yellow-500/30 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-yellow-300" />
                </div>
                <span className="text-white font-semibold text-lg">Plată</span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-gray-300">Cash la livrare</p>
                <p className="text-2xl font-bold text-yellow-300">
                  {course.amount.toLocaleString('ro-RO')} RON
                </p>
              </div>
            </div>
          )}

          {/* Observații */}
          {course.details.notes && (
            <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-2xl p-5 border border-amber-500/20 shadow-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-amber-500/30 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-300" />
                </div>
                <span className="text-white font-semibold text-lg">Observații</span>
              </div>
              <p className="text-gray-300 leading-relaxed">{course.details.notes}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {course.status !== 'livrat' && (
          <div className="sticky bottom-0 bg-gradient-to-t from-slate-900 to-slate-900/90 backdrop-blur-lg p-5 sm:p-6 border-t border-white/5">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  onClose();
                  onCMRUpload(course.id);
                }}
                className="flex-1 bg-gradient-to-r from-orange-500/30 to-orange-600/20 hover:from-orange-500/40 hover:to-orange-600/30 text-orange-200 border border-orange-500/40 rounded-xl py-4 px-6 font-semibold flex items-center justify-center space-x-2 transition-all active:scale-95 shadow-lg"
              >
                <Camera className="w-5 h-5" />
                <span>Încarcă CMR</span>
              </button>

              <button
                onClick={() => {
                  onClose();
                  onUpdateStatus(course.id, course.status);
                }}
                className="flex-1 bg-gradient-to-r from-green-500/30 to-green-600/20 hover:from-green-500/40 hover:to-green-600/30 text-green-200 border border-green-500/40 rounded-xl py-4 px-6 font-semibold flex items-center justify-center space-x-2 transition-all active:scale-95 shadow-lg"
              >
                <CheckCircle className="w-5 h-5" />
                <span>{getStatusButtonText(course.status)}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};